import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { getApiBaseUrl } from '../../lib/apiBase';
import Seo from '../Seo';

const API_BASE_URL = getApiBaseUrl();

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  service: '',
  message: '',
};

const ContactUs = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Server error.');
      }
      setStatus('success');
      setForm(initialForm);
    } catch (error) {
      const isTimeout = error?.name === 'AbortError';
      const message = isTimeout
        ? 'Server took too long to respond. Please try again.'
        : (error?.message || 'Failed to submit inquiry.');
      setErrorMessage(message);
      setStatus('error');
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title="Contact Us"
        description="Get in touch with Libsys Inc — sales, staffing, partnerships, and support. Offices in New Jersey, USA and Hyderabad, India. Response within one business day."
        path="/about-us/contact-us"
      />
      <section className="pt-6 pb-8 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-strategic-tagline text-[10px] font-black uppercase tracking-[0.1em] mb-2">Contact Us</p>
          <h1 className="text-[26px] lg:text-[32px] font-black text-primary leading-tight mb-2">
            Let&apos;s Discuss Your Requirements
          </h1>
          <p className="text-slate-600 text-tiny font-medium leading-relaxed max-w-4xl">
            Share your details and project goals. Our team will review your request and reach out quickly.
          </p>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-[0_8px_24px_-18px_rgba(15,23,42,0.3)]">
            <div className="px-5 lg:px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="text-[20px] font-black text-primary">Contact Form</h2>
              <p className="text-slate-600 text-[11px] font-medium mt-0.5">
                Provide accurate details for a quick response.
              </p>
            </div>

            {status === 'success' ? (
              <div className="mx-6 lg:mx-8 mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-emerald-700 text-sm font-bold">Thank you. Your details were submitted successfully.</p>
                <p className="text-emerald-700/90 text-sm font-medium mt-1">We have sent your inquiry to our email and will contact you soon.</p>
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="p-5 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-tiny font-bold text-slate-700 mb-2">Full name *</label>
                  <input
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-subtext bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary placeholder:font-normal placeholder:text-slate-300"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-tiny font-bold text-slate-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-subtext bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary placeholder:font-normal placeholder:text-slate-300"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-tiny font-bold text-slate-700 mb-2">Phone *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-subtext bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary placeholder:font-normal placeholder:text-slate-300"
                    placeholder="+1 000 000 0000"
                  />
                </div>
                <div>
                  <label className="block text-tiny font-bold text-slate-700 mb-2">Company</label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-subtext bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary placeholder:font-normal placeholder:text-slate-300"
                    placeholder="Organization name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-tiny font-bold text-slate-700 mb-2">Service interest</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary placeholder:font-normal placeholder:text-slate-300"
                >
                  <option value="">Select service</option>
                  <option value="AI & Machine Learning">AI & Machine Learning</option>
                  <option value="SAP">SAP</option>
                  <option value="Cloud & DevOps">Cloud & DevOps</option>
                  <option value="Product Development">Product Development</option>
                  <option value="Web Technologies">Web Technologies</option>
                  <option value="Strategic Staffing">Strategic Staffing</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="block text-tiny font-bold text-slate-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-md text-subtext resize-none bg-white focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary"
                  placeholder="Share your requirement in detail..."
                />
              </div>

              {status === 'error' ? (
                <p className="text-red-500 text-[12px] font-semibold bg-red-50 border border-red-200 px-4 py-3 rounded-lg">
                  {errorMessage || 'Unable to submit right now. Please try again.'}
                </p>
              ) : null}

               <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full sm:w-auto px-8 py-3 bg-primary text-white font-black text-tiny tracking-[0.04em] rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2 transition disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting...
                  </>
                ) : 'Submit Inquiry'}
              </button>
            </form>
          </div>

          <aside className="lg:col-span-5 border border-slate-200 rounded-2xl p-5 lg:p-6 bg-white shadow-[0_8px_24px_-18px_rgba(15,23,42,0.3)] h-fit">
            <h3 className="text-lg font-black text-primary mb-3">Contact Details</h3>
            <div className="space-y-4">
              <a
                href="tel:630-799-1556"
                className="flex items-start gap-2.5 group"
              >
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-primary flex-shrink-0">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 mb-0.5">Reach Us At</p>
                  <p className="text-slate-700 font-bold text-tiny group-hover:text-primary">630-799-1556</p>
                </div>
              </a>
 
              <a
                href="mailto:Info@LibsysInc.Com"
                className="flex items-start gap-2.5 group"
              >
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-primary flex-shrink-0">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 mb-0.5">Email</p>
                  <p className="text-slate-700 font-bold text-tiny group-hover:text-primary">Info@LibsysInc.Com</p>
                </div>
              </a>
 
              <a
                href="https://www.google.com/maps/search/?api=1&query=2020+Calamos+Ct+Suite+200+Naperville+IL+60563"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2.5 group"
              >
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-primary flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 mb-0.5">Corporate Office</p>
                  <p className="text-slate-700 font-bold text-tiny group-hover:text-primary">
                    2020 Calamos Ct, Suite #200,<br />Naperville IL 60563, USA
                  </p>
                </div>
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
